import { Map } from 'immutable';
import { IDispatchId, IRequestError } from '../../utils/helpers/redux.helper';
import { ITriggerPayPalOnboardingCanvasPayload } from '../payments-data/payments-data.actions';
import { Settings } from './settings.model';
import { TaxYearStart } from './sub-models/company-settings/tax-year-start.model';
import { TrackingScreenNamesEnum } from '../tracking/tracking.data';
import {
  I2gMoneyWebview,
  I2gMoneyContext,
  I2gMoneyAccountSummary,
  I2gMoneyStatement,
} from './i2g-money.data';

// action enums
export enum ActionsEnum {
  FETCH_SETTINGS = 'FETCH_SETTINGS',
  FETCH_SETTINGS_SUCCESS = 'FETCH_SETTINGS_SUCCESS',
  FETCH_SETTINGS_ERROR = 'FETCH_SETTINGS_ERROR',
  UPDATE_SETTINGS = 'UPDATE_SETTINGS',
  RESET_EDITING_SETTINGS = 'RESET_EDITING_SETTINGS',
  SET_EDITING_SETTINGS_TO_CURRENT = 'SET_EDITING_SETTINGS_TO_CURRENT',
  UPDATE_EDITING_SETTINGS = 'UPDATE_EDITING_SETTINGS',
  UPDATE_COUNTRY = 'UPDATE_COUNTRY',
  UPDATE_COUNTRY_SUCCESS = 'UPDATE_COUNTRY_SUCCESS',
  UPDATE_CURRENCY = 'UPDATE_CURRENCY',
  UPDATE_CURRENCY_SUCCESS = 'UPDATE_CURRENCY_SUCCESS',
  UPDATE_LOCALE = 'UPDATE_LOCALE',
  UPDATE_LOCALE_SUCCESS = 'UPDATE_LOCALE_SUCCESS',
  UPDATE_BANK_TRANSFER = 'UPDATE_BANK_TRANSFER',
  UPDATE_CARD_PAYMENTS_SETTINGS = 'UPDATE_CARD_PAYMENTS_SETTINGS',
  UPDATE_PAYPAL_SETTINGS = 'UPDATE_PAYPAL_SETTINGS',
  UPDATE_INITIAL_PAYMENT_RECEIPTS_SETTINGS = 'UPDATE_INITIAL_PAYMENT_RECEIPTS_SETTINGS',
  UPDATE_MFA_SETTINGS = 'UPDATE_MFA_SETTINGS',
  SAVE_SETTINGS_NETWORK = 'SAVE_SETTINGS_NETWORK',
  SAVE_SETTINGS_NETWORK_SUCCESS = 'SAVE_SETTINGS_NETWORK_SUCCESS',
  SAVE_SETTINGS_NETWORK_ERROR = 'SAVE_SETTINGS_NETWORK_ERROR',
  SAVE_CURRENT_SETTINGS_NETWORK = 'SAVE_CURRENT_SETTINGS_NETWORK',
  SAVE_EDITING_SETTINGS = 'SAVE_EDITING_SETTINGS',
  EMIT_TAX_YEAR_CHANGED = 'EMIT_TAX_YEAR_CHANGED',
  EMIT_CURRENCY_CODE_CHANGED = 'EMIT_CURRENCY_CODE_CHANGED',
  SET_SETTINGS_DISPATCHED_RESPONSE = 'SET_SETTINGS_DISPATCHED_RESPONSE',
  FETCH_STRIPE_ONBOARDING_URL = 'FETCH_STRIPE_ONBOARDING_URL',
  SET_UNDERWRITING_URL = 'SET_UNDERWRITING_URL',
  FETCH_PAYPAL_ONBOARDING_URL = 'FETCH_PAYPAL_ONBOARDING_URL',
  FETCH_PAYPAL_ONBOARDING_URL_SUCCESS = 'FETCH_PAYPAL_ONBOARDING_URL_SUCCESS',
  FETCH_I2G_MONEY_WEBVIEW = 'FETCH_I2G_MONEY_WEBVIEW',
  FETCH_I2G_MONEY_WEBVIEW_SUCCESS = 'FETCH_I2G_MONEY_WEBVIEW_SUCCESS',
  FETCH_I2G_MONEY_CONTEXT = 'FETCH_I2G_MONEY_CONTEXT',
  FETCH_I2G_MONEY_CONTEXT_SUCCESS = 'FETCH_I2G_MONEY_CONTEXT_SUCCESS',
  UPDATE_I2G_MONEY_CONTEXT = 'UPDATE_I2G_MONEY_CONTEXT',
  UPDATE_I2G_MONEY_CONTEXT_SUCCESS = 'UPDATE_I2G_MONEY_CONTEXT_SUCCESS',
  OPEN_I2G_MONEY_ONBOARDING = 'OPEN_I2G_MONEY_ONBOARDING',
  CLOSE_I2G_MONEY_ONBOARDING = 'CLOSE_I2G_MONEY_ONBOARDING',
  INITIALIZE_PLAID = 'INITIALIZE_PLAID',
  LINK_EXTERNAL_BANK_ACCOUNT = 'LINK_EXTERNAL_BANK_ACCOUNT',
  UNLINK_EXTERNAL_BANK_ACCOUNT = 'UNLINK_EXTERNAL_BANK_ACCOUNT',
  START_LINKING_EXTERNAL_BANK_ACCOUNT = 'START_LINKING_EXTERNAL_BANK_ACCOUNT',
  FINISH_LINKING_EXTERNAL_BANK_ACCOUNT = 'FINISH_LINKING_EXTERNAL_BANK_ACCOUNT',
  FETCH_I2G_MONEY_STATEMENTS = 'FETCH_I2G_MONEY_STATEMENTS',
  FETCH_I2G_MONEY_STATEMENTS_SUCCESS = 'FETCH_I2G_MONEY_STATEMENTS_SUCCESS',
  FETCH_I2G_MONEY_ACCOUNT_SUMMARY = 'FETCH_I2G_MONEY_ACCOUNT_SUMMARY',
  FETCH_I2G_MONEY_ACCOUNT_SUMMARY_SUCCESS = 'FETCH_I2G_MONEY_ACCOUNT_SUMMARY_SUCCESS',

  // PayPal
  UNLINK_PAYPAL_ACCOUNT = 'UNLINK_PAYPAL_ACCOUNT',
  UNLINK_PAYPAL_ACCOUNT_SUCCESS = 'UNLINK_PAYPAL_ACCOUNT_SUCCESS',
  UNLINK_PAYPAL_ACCOUNT_ERROR = 'UNLINK_PAYPAL_ACCOUNT_ERROR',
}

// action payloads
export interface ISettingsProps {
  settingsProps: Map<string, any> | Settings;
}

export interface ISettings {
  settings: Settings;
}

export interface IUpdateEditingSettings {
  updateEditingSettings: boolean;
}

export interface IFetchSettings extends IDispatchId, IUpdateEditingSettings {}

export interface ICurrencyCode {
  currency: string;
}

export interface ICountry extends IDispatchId, IUpdateEditingSettings {
  country: string;
}

export interface ICurrency
  extends ICurrencyCode,
    IDispatchId,
    IUpdateEditingSettings {}

export interface ILocale extends IDispatchId, IUpdateEditingSettings {
  locale: string;
}

export interface IUpdateBankTransfer extends IDispatchId {
  enabled: boolean;
  href: string;
}

export interface IUpdateCardPaymentsSettings extends IDispatchId {
  enabled: boolean;
  href: string;
}

export interface IUpdatePaypalSettings extends IDispatchId {
  enabled: boolean;
}

export interface IUpdateInitialPaymentReceiptsSettings {
  enabled: boolean;
}

export interface IUpdateMfaSettings {
  enabled: boolean;
}

export interface IUpdateSettings extends ISettingsProps {
  resetOriginalEditingSettings: boolean;
  updateEditingSettings: boolean;
}

export interface ISettingsPayload extends ISettings, IDispatchId {}

export interface ISaveSettingsNetworkSuccess extends ISettings, IDispatchId {
  previousSettings: Settings | null;
}

export interface IUpdateTaxYear {
  taxYearStart: TaxYearStart;
}

export interface ISetSettingsDispatchedResponse extends IRequestError {
  entity: any;
}

export interface ITriggerPostPurchaseSurvey {
  title: string;
  header: string;
  trackingScreenName: TrackingScreenNamesEnum;
}

export interface IFetchStripeOnboardingUrl {
  enableCardPayments: boolean;
  returnUrl: string;
}

export interface ISetUnderwritingUrl {
  underwritingUrl: string;
}

export interface IPayPalOnboardingUrlPayload {
  paypalOnboardingUrl: string;
}
// action definitions
export interface FetchSettings {
  type: ActionsEnum.FETCH_SETTINGS;
  payload: IFetchSettings;
}

export interface FetchSettingsSuccess {
  type: ActionsEnum.FETCH_SETTINGS_SUCCESS;
  payload: IDispatchId;
}

export interface FetchSettingsError {
  type: ActionsEnum.FETCH_SETTINGS_ERROR;
  payload: IRequestError;
}

export interface UpdateSettings {
  type: ActionsEnum.UPDATE_SETTINGS;
  payload: IUpdateSettings;
}

export interface ResetEditingSettings {
  type: ActionsEnum.RESET_EDITING_SETTINGS;
}

export interface SetEditingSettingsToCurrent {
  type: ActionsEnum.SET_EDITING_SETTINGS_TO_CURRENT;
}

export interface UpdateEditingSettings {
  type: ActionsEnum.UPDATE_EDITING_SETTINGS;
  payload: ISettingsProps;
}

export interface UpdateCountry {
  type: ActionsEnum.UPDATE_COUNTRY;
  payload: ICountry;
}

export interface UpdateCountrySuccess {
  type: ActionsEnum.UPDATE_COUNTRY_SUCCESS;
  payload: ISettingsPayload;
}

export interface UpdateCurrency {
  type: ActionsEnum.UPDATE_CURRENCY;
  payload: ICurrency;
}

export interface UpdateCurrencySuccess {
  type: ActionsEnum.UPDATE_CURRENCY_SUCCESS;
  payload: ISettingsPayload;
}

export interface UpdateLocale {
  type: ActionsEnum.UPDATE_LOCALE;
  payload: ILocale;
}

export interface UpdateLocaleSuccess {
  type: ActionsEnum.UPDATE_LOCALE_SUCCESS;
  payload: ISettingsPayload;
}

export interface UpdateBankTransfer {
  type: ActionsEnum.UPDATE_BANK_TRANSFER;
  payload: IUpdateBankTransfer;
}

export interface UpdateCardPaymentsSettings {
  type: ActionsEnum.UPDATE_CARD_PAYMENTS_SETTINGS;
  payload: IUpdateCardPaymentsSettings;
}

export interface UpdatePaypalSettings {
  type: ActionsEnum.UPDATE_PAYPAL_SETTINGS;
  payload: IUpdatePaypalSettings;
}

export interface UpdateInitialPaymentReceiptsSettings {
  type: ActionsEnum.UPDATE_INITIAL_PAYMENT_RECEIPTS_SETTINGS;
  payload: IUpdateInitialPaymentReceiptsSettings;
}

export interface UpdateMfaSettings {
  type: ActionsEnum.UPDATE_MFA_SETTINGS;
  payload: IUpdateMfaSettings;
}

export interface SaveSettingsNetwork {
  type: ActionsEnum.SAVE_SETTINGS_NETWORK;
  payload: ISettingsPayload;
}

export interface SaveSettingsNetworkSuccess {
  type: ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS;
  payload: ISaveSettingsNetworkSuccess;
}

export interface SaveSettingsNetworkError {
  type: ActionsEnum.SAVE_SETTINGS_NETWORK_ERROR;
  payload: IRequestError;
}

export interface SaveCurrentSettingsNetwork {
  type: ActionsEnum.SAVE_CURRENT_SETTINGS_NETWORK;
  payload: IDispatchId;
}

export interface SaveEditingSettings {
  type: ActionsEnum.SAVE_EDITING_SETTINGS;
  payload: IDispatchId;
}

export interface EmitTaxYearChanged {
  type: ActionsEnum.EMIT_TAX_YEAR_CHANGED;
}

export interface EmitCurrencyCodeChanged {
  type: ActionsEnum.EMIT_CURRENCY_CODE_CHANGED;
  payload: ICurrencyCode;
}

export interface SetSettingsDispatchedResponse {
  type: ActionsEnum.SET_SETTINGS_DISPATCHED_RESPONSE;
  payload: ISetSettingsDispatchedResponse;
}

export interface FetchStripeOnboardingUrl {
  type: ActionsEnum.FETCH_STRIPE_ONBOARDING_URL;
  payload: IFetchStripeOnboardingUrl;
}

export interface SetUnderwritingUrl {
  type: ActionsEnum.SET_UNDERWRITING_URL;
  payload: ISetUnderwritingUrl;
}

export interface IFetchPayPalOnboardingUrl {
  type: ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL;
  payload: ITriggerPayPalOnboardingCanvasPayload;
}

export interface IFetchPayPalOnboardingUrlSuccess {
  type: ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL_SUCCESS;
  payload: IPayPalOnboardingUrlPayload;
}

export interface IUnlinkPayPalAccount {
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT;
}

export interface IUnlinkPayPalAccountSuccess {
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT_SUCCESS;
}

export interface IUnlinkPayPalAccountError {
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT_ERROR;
  payload: {
    error: Error;
  };
}

// action creators
export const fetchSettings = (
  dispatchId: string,
  shouldUpdateEditingSettings: boolean = false
): FetchSettings => ({
  type: ActionsEnum.FETCH_SETTINGS,
  payload: {
    dispatchId,
    updateEditingSettings: shouldUpdateEditingSettings,
  },
});

export const fetchSettingsSuccess = (
  dispatchId: string
): FetchSettingsSuccess => ({
  type: ActionsEnum.FETCH_SETTINGS_SUCCESS,
  payload: {
    dispatchId,
  },
});

export const fetchSettingsError = (
  dispatchId: string,
  error: Error
): FetchSettingsError => ({
  type: ActionsEnum.FETCH_SETTINGS_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const updateSettings = (
  settingsProps: Map<string, any> | Settings,
  resetOriginalEditingSettings: boolean = false,
  shouldUpdateEditingSettings: boolean = false
): UpdateSettings => ({
  type: ActionsEnum.UPDATE_SETTINGS,
  payload: {
    settingsProps,
    resetOriginalEditingSettings,
    updateEditingSettings: shouldUpdateEditingSettings,
  },
});

export const resetEditingSettings = (): ResetEditingSettings => ({
  type: ActionsEnum.RESET_EDITING_SETTINGS,
});

export const setEditingSettingsToCurrent = (): SetEditingSettingsToCurrent => ({
  type: ActionsEnum.SET_EDITING_SETTINGS_TO_CURRENT,
});

export const updateEditingSettings = (
  settingsProps: Settings | Map<string, any>
): UpdateEditingSettings => ({
  type: ActionsEnum.UPDATE_EDITING_SETTINGS,
  payload: {
    settingsProps,
  },
});

export const updateCountry = (
  dispatchId: string,
  country: string,
  shouldUpdateEditingSettings: boolean = false
): UpdateCountry => ({
  type: ActionsEnum.UPDATE_COUNTRY,
  payload: {
    dispatchId,
    country,
    updateEditingSettings: shouldUpdateEditingSettings,
  },
});

export const updateCountrySuccess = (
  dispatchId: string,
  settings: Settings
): UpdateCountrySuccess => ({
  type: ActionsEnum.UPDATE_COUNTRY_SUCCESS,
  payload: {
    dispatchId,
    settings,
  },
});

export const updateCurrency = (
  dispatchId: string,
  currency: string,
  shouldUpdateEditingSettings: boolean = false
): UpdateCurrency => ({
  type: ActionsEnum.UPDATE_CURRENCY,
  payload: {
    dispatchId,
    currency,
    updateEditingSettings: shouldUpdateEditingSettings,
  },
});

export const updateCurrencySuccess = (
  dispatchId: string,
  settings: Settings
): UpdateCurrencySuccess => ({
  type: ActionsEnum.UPDATE_CURRENCY_SUCCESS,
  payload: {
    dispatchId,
    settings,
  },
});

export const updateLocale = (
  dispatchId: string,
  locale: string,
  shouldUpdateEditingSettings: boolean = false
): UpdateLocale => ({
  type: ActionsEnum.UPDATE_LOCALE,
  payload: {
    dispatchId,
    locale,
    updateEditingSettings: shouldUpdateEditingSettings,
  },
});

export const updateLocaleSuccess = (
  dispatchId: string,
  settings: Settings
): UpdateLocaleSuccess => ({
  type: ActionsEnum.UPDATE_LOCALE_SUCCESS,
  payload: {
    dispatchId,
    settings,
  },
});

export const updateBankTransfer = (
  dispatchId: string,
  enabled: boolean,
  href: string
): UpdateBankTransfer => ({
  type: ActionsEnum.UPDATE_BANK_TRANSFER,
  payload: {
    dispatchId,
    enabled,
    href,
  },
});

export const updateCardPaymentsSettings = (
  dispatchId: string,
  enabled: boolean,
  href: string
): UpdateCardPaymentsSettings => ({
  type: ActionsEnum.UPDATE_CARD_PAYMENTS_SETTINGS,
  payload: {
    dispatchId,
    enabled,
    href,
  },
});

export const updatePaypalSettings = (
  dispatchId: string,
  enabled: boolean
): UpdatePaypalSettings => ({
  type: ActionsEnum.UPDATE_PAYPAL_SETTINGS,
  payload: {
    dispatchId,
    enabled,
  },
});

export const updateInitialPaymentReceiptsSettings = (
  enabled: boolean
): UpdateInitialPaymentReceiptsSettings => ({
  type: ActionsEnum.UPDATE_INITIAL_PAYMENT_RECEIPTS_SETTINGS,
  payload: {
    enabled,
  },
});

export const saveSettingsNetwork = (
  dispatchId: string,
  settings: Settings
): SaveSettingsNetwork => ({
  type: ActionsEnum.SAVE_SETTINGS_NETWORK,
  payload: {
    dispatchId,
    settings,
  },
});

export const updateMfaSettings = (enabled: boolean): UpdateMfaSettings => ({
  type: ActionsEnum.UPDATE_MFA_SETTINGS,
  payload: {
    enabled,
  },
});

export const saveSettingsNetworkSuccess = (
  dispatchId: string,
  settings: Settings,
  previousSettings: Settings
): SaveSettingsNetworkSuccess => ({
  type: ActionsEnum.SAVE_SETTINGS_NETWORK_SUCCESS,
  payload: {
    dispatchId,
    settings,
    previousSettings,
  },
});

export const saveSettingsNetworkError = (
  dispatchId: string,
  error: Error
): SaveSettingsNetworkError => ({
  type: ActionsEnum.SAVE_SETTINGS_NETWORK_ERROR,
  payload: {
    dispatchId,
    error,
  },
});

export const saveCurrentSettingsNetwork = (
  dispatchId: string
): SaveCurrentSettingsNetwork => ({
  type: ActionsEnum.SAVE_CURRENT_SETTINGS_NETWORK,
  payload: {
    dispatchId,
  },
});

export const saveEditingSettings = (
  dispatchId: string
): SaveEditingSettings => ({
  type: ActionsEnum.SAVE_EDITING_SETTINGS,
  payload: {
    dispatchId,
  },
});

export const emitTaxYearChanged = (): EmitTaxYearChanged => ({
  type: ActionsEnum.EMIT_TAX_YEAR_CHANGED,
});

export const emitCurrencyCodeChanged = (
  currency: string
): EmitCurrencyCodeChanged => ({
  type: ActionsEnum.EMIT_CURRENCY_CODE_CHANGED,
  payload: {
    currency,
  },
});

export const setSettingsDispatchedResponse = (
  dispatchId: string,
  entity: any,
  error: Error = null
): SetSettingsDispatchedResponse => ({
  type: ActionsEnum.SET_SETTINGS_DISPATCHED_RESPONSE,
  payload: {
    dispatchId,
    entity,
    error,
  },
});

export const fetchStripeOnboardingUrl = (
  enableCardPayments: boolean,
  returnUrl: string
): FetchStripeOnboardingUrl => ({
  type: ActionsEnum.FETCH_STRIPE_ONBOARDING_URL,
  payload: {
    enableCardPayments,
    returnUrl,
  },
});

export const setUnderwritingUrl = (
  underwritingUrl: string
): SetUnderwritingUrl => ({
  type: ActionsEnum.SET_UNDERWRITING_URL,
  payload: {
    underwritingUrl,
  },
});

export const fetchPayPalOnboardingUrl = (
  returnUrl?: string
): IFetchPayPalOnboardingUrl => ({
  type: ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL,
  payload: {
    returnUrl,
  },
});

export const fetchPayPalOnboardingUrlSuccess = (
  paypalOnboardingUrl: string
): IFetchPayPalOnboardingUrlSuccess => ({
  type: ActionsEnum.FETCH_PAYPAL_ONBOARDING_URL_SUCCESS,
  payload: {
    paypalOnboardingUrl,
  },
});

/** I2G MONEY ONBOARDING */
export interface FetchI2gMoneyWebview {
  type: ActionsEnum.FETCH_I2G_MONEY_WEBVIEW;
}

type FetchI2gMoneyWebviewSuccessPayload = I2gMoneyWebview;
export interface FetchI2gMoneyWebviewSuccess {
  type: ActionsEnum.FETCH_I2G_MONEY_WEBVIEW_SUCCESS;
  payload: FetchI2gMoneyWebviewSuccessPayload;
}

export interface FetchI2gMoneyContext {
  type: ActionsEnum.FETCH_I2G_MONEY_CONTEXT;
}

type FetchI2gMoneyContextSuccessPayload = I2gMoneyContext;

export interface FetchI2gMoneyContextSuccess {
  type: ActionsEnum.FETCH_I2G_MONEY_CONTEXT_SUCCESS;
  payload: FetchI2gMoneyContextSuccessPayload;
}

export interface UpdateI2gMoneyContext {
  type: ActionsEnum.UPDATE_I2G_MONEY_CONTEXT;
  payload: boolean;
}

export interface UpdateI2gMoneyContextSuccess {
  type: ActionsEnum.UPDATE_I2G_MONEY_CONTEXT_SUCCESS;
  payload: FetchI2gMoneyContextSuccessPayload['settings'];
}

export const fetchI2gMoneyWebview = (): FetchI2gMoneyWebview => ({
  type: ActionsEnum.FETCH_I2G_MONEY_WEBVIEW,
});

export const fetchI2gMoneyWebviewSuccess = (
  data: I2gMoneyWebview
): FetchI2gMoneyWebviewSuccess => ({
  type: ActionsEnum.FETCH_I2G_MONEY_WEBVIEW_SUCCESS,
  payload: data,
});

export const fetchI2gMoneyContext = (): FetchI2gMoneyContext => ({
  type: ActionsEnum.FETCH_I2G_MONEY_CONTEXT,
});

export const fetchI2gMoneyContextSuccess = (
  data: I2gMoneyContext
): FetchI2gMoneyContextSuccess => ({
  type: ActionsEnum.FETCH_I2G_MONEY_CONTEXT_SUCCESS,
  payload: data,
});

export const updateI2gMoneyContext = (
  enabled: boolean
): UpdateI2gMoneyContext => ({
  type: ActionsEnum.UPDATE_I2G_MONEY_CONTEXT,
  payload: enabled,
});

export const updateI2gMoneyContextSuccess = (
  data: I2gMoneyContext['settings']
): UpdateI2gMoneyContextSuccess => ({
  type: ActionsEnum.UPDATE_I2G_MONEY_CONTEXT_SUCCESS,
  payload: data,
});

export interface OpenI2gMoneyOnboarding {
  type: ActionsEnum.OPEN_I2G_MONEY_ONBOARDING;
  payload: {
    trackingScreenName: string;
  };
}

export const openI2gMoneyOnboarding = (
  trackingScreenName: string
): OpenI2gMoneyOnboarding => ({
  type: ActionsEnum.OPEN_I2G_MONEY_ONBOARDING,
  payload: { trackingScreenName },
});
export const closeI2gMoneyOnboarding = () => ({
  type: ActionsEnum.CLOSE_I2G_MONEY_ONBOARDING,
});

export const initializePlaid = () => ({
  type: ActionsEnum.INITIALIZE_PLAID,
});

export const startProcessing = () => ({
  type: ActionsEnum.START_LINKING_EXTERNAL_BANK_ACCOUNT,
});

export const finishProcessing = () => ({
  type: ActionsEnum.FINISH_LINKING_EXTERNAL_BANK_ACCOUNT,
});
export interface SubmitPlaidLinkDetailsPayload {
  publicToken: string;
  accountId: string;
}
export interface SubmitPlaidLinkDetails {
  type: ActionsEnum.LINK_EXTERNAL_BANK_ACCOUNT;
  payload: SubmitPlaidLinkDetailsPayload;
}

export const submitPlaidLinkDetails = (
  data: SubmitPlaidLinkDetailsPayload
): SubmitPlaidLinkDetails => ({
  type: ActionsEnum.LINK_EXTERNAL_BANK_ACCOUNT,
  payload: data,
});

export interface UnlinkExternalBankAccount {
  type: ActionsEnum.UNLINK_EXTERNAL_BANK_ACCOUNT;
}

export const unlinkExternalBankAccount = (): UnlinkExternalBankAccount => ({
  type: ActionsEnum.UNLINK_EXTERNAL_BANK_ACCOUNT,
});

export interface FetchI2gMoneyAccountSummary {
  type: ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY;
}

export const fetchI2gMoneyAccountSummary = (): FetchI2gMoneyAccountSummary => ({
  type: ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY,
});

export interface FetchI2gMoneyAccountSummarySuccess {
  type: ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY_SUCCESS;
  payload: I2gMoneyAccountSummary;
}

export const fetchI2gMoneyAccountSummarySuccess = (
  accountSummary: I2gMoneyAccountSummary
): FetchI2gMoneyAccountSummarySuccess => ({
  type: ActionsEnum.FETCH_I2G_MONEY_ACCOUNT_SUMMARY_SUCCESS,
  payload: accountSummary,
});

export interface FetchI2gMoneyStatements {
  type: ActionsEnum.FETCH_I2G_MONEY_STATEMENTS;
}

export const fetchI2gMoneyStatements = (): FetchI2gMoneyStatements => ({
  type: ActionsEnum.FETCH_I2G_MONEY_STATEMENTS,
});

export interface FetchI2gMoneyStatementsSuccess {
  type: ActionsEnum.FETCH_I2G_MONEY_STATEMENTS_SUCCESS;
  payload: I2gMoneyStatement[];
}

export const fetchI2gMoneyStatementsSuccess = (
  statements: I2gMoneyStatement[]
): FetchI2gMoneyStatementsSuccess => ({
  type: ActionsEnum.FETCH_I2G_MONEY_STATEMENTS_SUCCESS,
  payload: statements,
});

export const unlinkPayPalAccount = (): IUnlinkPayPalAccount => ({
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT,
});

export const unlinkPayPalAccountSuccess = (): IUnlinkPayPalAccountSuccess => ({
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT_SUCCESS,
});

export const unlinkPayPalAccountError = (
  error: Error
): IUnlinkPayPalAccountError => ({
  type: ActionsEnum.UNLINK_PAYPAL_ACCOUNT_ERROR,
  payload: {
    error,
  },
});
